import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useProject } from '@/contexts/ProjectContext';
import { ChevronRight } from 'lucide-react';
import * as api from '@/lib/api';

/**
 * StatusDot color for compact list
 */
function statusDotColor(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-text-tertiary';
    case 'approved':
      return 'bg-status-approved';
    case 'in-progress':
      return 'bg-primary';
    case 'converging':
      return 'bg-warning';
    case 'complete':
      return 'bg-success';
    case 'conditional':
      return 'bg-status-conditional';
    case 'rework':
      return 'bg-error';
    default:
      return 'bg-border';
  }
}

type StatusFilter = 'all' | 'active' | 'rework' | 'complete';

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Rework', value: 'rework' },
  { label: 'Complete', value: 'complete' },
];

/**
 * OutcomeListPanel
 *
 * Renders a compact, filterable list of outcomes with status at a glance.
 * Clicking an outcome navigates to /outcomes/:id.
 *
 * Filters:
 * - All: show all outcomes
 * - Active: show draft, approved, in-progress, converging
 * - Rework: show rework status
 * - Complete: show complete status
 */
export function OutcomeListPanel(): React.ReactElement | null {
  const { selectedProjectId } = useProject();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const { data: outcomes, isLoading } = useQuery({
    queryKey: ['outcomes', selectedProjectId],
    queryFn: () => {
      if (!selectedProjectId) throw new Error('No project selected');
      return api.listOutcomes(selectedProjectId);
    },
    enabled: !!selectedProjectId,
    staleTime: 10_000,
    refetchInterval: 10_000,
  });

  const filtered = (outcomes || []).filter(outcome => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') {
      return ['draft', 'approved', 'in-progress', 'converging'].includes(outcome.status);
    }
    return outcome.status === statusFilter;
  });

  if (!selectedProjectId) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
          Outcomes
        </span>
        <button
          onClick={() => navigate('/outcomes')}
          className="rounded p-1 transition-colors hover:bg-surface-elevated"
          title="View all outcomes"
        >
          <ChevronRight className="h-3.5 w-3.5 text-text-secondary" />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="mb-2 flex gap-1">
        {STATUS_FILTERS.map(filter => (
          <button
            key={filter.value}
            onClick={() => {
              setStatusFilter(filter.value);
            }}
            className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
              statusFilter === filter.value
                ? 'bg-primary text-primary-foreground'
                : 'border border-border text-text-secondary hover:bg-surface-elevated'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 space-y-1 overflow-y-auto">
        {isLoading && <div className="text-xs text-text-secondary">Loading outcomes...</div>}

        {!isLoading && filtered.length === 0 && (
          <div className="rounded bg-surface-elevated/50 p-2 text-center text-[10px] text-text-tertiary">
            {statusFilter === 'all'
              ? 'No outcomes yet — click Discover'
              : 'No outcomes with this status'}
          </div>
        )}

        {!isLoading &&
          filtered.map(outcome => (
            <button
              key={outcome.id}
              onClick={() => {
                navigate(`/outcomes/${outcome.id}`);
              }}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left transition-colors hover:bg-surface-elevated"
            >
              {/* Status dot */}
              <div
                className={`h-2 w-2 flex-shrink-0 rounded-full ${statusDotColor(outcome.status)}`}
              />

              {/* Title */}
              <span className="flex-1 truncate text-xs text-text-primary">{outcome.title}</span>

              {/* Convergence score */}
              {outcome.convergenceScore !== null && (
                <span
                  className={`flex-shrink-0 text-[10px] font-medium ${
                    outcome.convergenceScore >= 90
                      ? 'text-success'
                      : outcome.convergenceScore >= 70
                        ? 'text-warning'
                        : 'text-error'
                  }`}
                >
                  {outcome.convergenceScore}%
                </span>
              )}
            </button>
          ))}
      </div>
    </div>
  );
}
